import React from 'react';

import { Body } from '@depop/web-thrift-ui/Typography';
import { LoadingBall } from '@depop/web-ui-kit/LoadingBall';
import { useTranslations } from 'next-intl';

import styles from './styles.module.css';

import { ErrorMessage } from '@/components/ErrorMessage';
import { InfiniteScroll } from '@/components/InfiniteScroll';
import { ProductImpressionHandler } from '@/components/ProductImpressionHandler';
import { RecentlyViewedProductCard } from '@/components/RecentlyViewed/RecentlyViewedProductCard';
import { ModuleName } from '@/modules/impressions/types';
import { useRecentlyViewedTracking } from '@/modules/recentlyViewed/hooks/useRecentlyViewedTracking';
import {
  type LocallyStoredRecentlyViewedProduct,
  type RecentlyViewedProduct,
} from '@/modules/recentlyViewed/types';

type Props = {
  productId?: number;
  isLoading: boolean;
  isError: boolean;
  onEndReached: () => void;
  isFetchingNextPage: boolean;
  hasMore: boolean;
  products: (RecentlyViewedProduct | LocallyStoredRecentlyViewedProduct)[];
  pageSize: number;
  moduleOrigin?: string;
};

export function RecentlyViewedModalProducts({
  productId,
  isLoading,
  isError,
  onEndReached,
  isFetchingNextPage,
  hasMore,
  products,
  pageSize,
  moduleOrigin,
}: Props) {
  const tCommon = useTranslations('common');
  const { sendRecentlyViewedItemsInteractionAction } =
    useRecentlyViewedTracking(productId);

  function handleProductClick(selectedProductId: number) {
    sendRecentlyViewedItemsInteractionAction(
      'RecentlyViewedItemsModuleProductClickedInModal',
      selectedProductId
    );
  }

  if (isLoading) {
    return <LoadingBall />;
  }

  if (isError) {
    return (
      <ErrorMessage type="body" className={styles.error}>
        {tCommon('500.Message')}
      </ErrorMessage>
    );
  }

  if (!products.length) {
    return <Body variant="sm-regular">{tCommon('NoRecentlyViewedItems')}</Body>;
  }

  return (
    <InfiniteScroll
      onEndReached={onEndReached}
      hasMore={hasMore}
      loading={isFetchingNextPage}
      LoadingComponent={<LoadingBall />}
    >
      <ul className={styles.productList}>
        {products.map((product, index) => (
          <li key={product.id}>
            <ProductImpressionHandler
              index={index}
              isLastItem={index === products.length - 1 && !hasMore}
              pageNumber={Math.floor(index / pageSize) + 1}
              moduleName={ModuleName.RecentlyViewed}
            >
              <RecentlyViewedProductCard
                id={product.id}
                slug={product.slug}
                pricing={{
                  ...product.pricing,
                  currency: product.pricing.currency_name,
                }}
                sizes={product.sizes}
                pictures={product.pictures}
                preview={product.preview}
                brandName={product.brand_name}
                hasVideo={product.has_video}
                isLiked={product.is_liked}
                handleClick={() => handleProductClick(product.id)}
                moduleOrigin={moduleOrigin}
              />
            </ProductImpressionHandler>
          </li>
        ))}
      </ul>
    </InfiniteScroll>
  );
}
